import { useParams } from "react-router-dom";
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PermissionErrorPage from "../../pages/shared/permissionError";
import Editor from '@monaco-editor/react';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { questionService } from '../../services/api';
import { toast } from 'react-toastify';
import { storageService } from '../../services/storage';
import { USER_ROLES, TOPICS } from '../../utils/constants';
import LoadingSpinner from '../ui/LoadingSpinner';


export default function QuestionEditor(){
    const { id } = useParams();
    const [topic, setTopic] = useState("");
    const [difficulty, setDifficulty] = useState("");
    const [questionText, setQuestionText] = useState(''); 
    const [nextId, setNextId] = useState();
    const [code, setCode] = useState("");
    const [tests, setTests] = useState();
    const [allQuestions, setAllQuestions] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState({});
    const [isLoading, setIsLoading] = useState(false);


    const fetchQuestionData = async () => {
        setIsLoading(true);
        try {
            // Fetch all questions for dropdown
            const questionsData = await questionService.getQuestions();
            setAllQuestions(questionsData);
            
            // Fetch current question data
            const currentQuestionData = await questionService.getQuestion(id);
            const questionInfo = Array.isArray(currentQuestionData) && currentQuestionData.length > 0 
                ? currentQuestionData[0] 
                : currentQuestionData;
                
            setCurrentQuestion(questionInfo);
            setQuestionText(questionInfo.question || '');
            setTopic(questionInfo.topic || '');
            setDifficulty(questionInfo.difficulty || '');
            setTests(questionInfo.test_cases || '');
            setNextId(questionInfo.next_id || '');
            setCode(questionInfo.answer || '');
        } catch (error) {
            console.error('Error fetching question data:', error);
            alert('Failed to load question data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleNextQuestionChange = (e) => {
        setNextId(e.target.value);
    };

    const saveQuestionChanges = async (formData) => {
        setIsLoading(true);
        try {
            const questionData = {
                topic: formData.topic,
                question: formData.question,
                answer: formData.answer,
                test_cases: formData.testCases,
                function_name: formData.functionName,
                difficulty: formData.difficulty,
                next_id: formData.nextId
            };
            
            await questionService.updateQuestion(id, questionData);
            toast.success('Changes saved successfully!');
        } catch (error) {
            console.error('Error saving changes:', error);
            toast.error('Failed to save changes. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };
    
    
   
    useEffect(() => {
        fetchQuestionData();
    }, [id]);

    const handleTopicChange = (e) => {
        setTopic(e.target.value);
    };
    
    const handleDifficultyChange = (e) => {
        setDifficulty(e.target.value);
    };
    
    const handleQuestionTextChange = (value) => {
        setQuestionText(value);
    };
    
    const handleCodeChange = (value) => {
        setCode(value);
    };
    
    const handleTestsChange = (value) => {
        setTests(value);
    };
   
    return (
        <div>
            {(storageService.getRole() === USER_ROLES.INSTRUCTOR) ? (
            <>
                <h1>Edit Question</h1>
                <h3>Please fill out each of the following information to create a question. </h3>
                
                    {isLoading ? (
                        <LoadingSpinner text="Loading question data..." />
                    ) : (
                    <Form onSubmit={(e) => {
                        e.preventDefault();
                        const formData = {
                            topic,
                            question: questionText,
                            answer: code,
                            testCases: tests,
                            functionName: e.target.funcName.value,
                            difficulty,
                            nextId
                        };
                        saveQuestionChanges(formData);
                    }}>
                        

                        <div id="questionBoxes">
                        <label>ID:</label>
                        <input type="number" value = {id} name="id" readOnly/>
                        <Form.Group className="mb-3">
                            <Form.Label>Topic:</Form.Label>
                            <Col>
                                <select onChange={handleTopicChange} name="topic" value={topic}>
                                    <option value="" disabled>Choose a topic</option>
                                    {TOPICS.map(topicName => (
                                        <option key={topicName} value={topicName}>{topicName}</option>
                                    ))}
                                </select>
                            </Col>
                        </Form.Group>
                        {' '}
                        <label>Question: </label>
                        <ReactQuill 
                            theme="snow" 
                            style={{height: "100%", marginBottom: '1px'}} 
                            value={questionText} 
                            onChange={handleQuestionTextChange}
                        />
                        
                        {' '}
                        <label>Answer:</label>
                        <div id = "userCode">
                        <Editor 
                            id="editor" 
                            height="200" 
                            onChange={handleCodeChange} 
                            defaultLanguage="python" 
                            value={code} 
                            name="answer" 
                        />
                        </div>
                        

                        <label>Test Cases:</label>
                        <div id = "userCode">
                        <Editor 
                            id="editor" 
                            height="200" 
                            onChange={handleTestsChange} 
                            defaultLanguage="json" 
                            value={tests} 
                            name="test" 
                        />
                        </div>
                        

                        <label>Function Name: </label>
                        <input 
                            type="text" 
                            defaultValue={currentQuestion.function_name} 
                            name="funcName" 
                        />

                        
                        <Form.Group className="mb-3">
                            <Form.Label>Difficulty:</Form.Label>
                            <Col>
                                <select onChange={handleDifficultyChange} name="difficulty" value={difficulty}>
                                    <option disabled>Choose a difficulty</option>
                                    <option value="easy">Easy</option>
                                    <option value="medium">Medium</option>
                                    <option value="hard">Hard</option>
                                </select>
                            </Col>
                        </Form.Group>

                        <label>Next ID:</label>
                        <Form.Group className="mb-3">
                    <Col lg={10}>
                        <Form.Select 
                            aria-label="Question List" 
                            onChange={handleNextQuestionChange} 
                            value={nextId}
                        >
                            <option value="" disabled>Choose from the list of questions</option>
                            {allQuestions.map((questionItem) => (
                                <option key={questionItem.q_id} value={questionItem.q_id}>
                                    {questionItem.q_id}. {questionItem.function_name}
                                </option>    
                            ))}
                        </Form.Select> 
                    </Col>
                </Form.Group>
                    <Button 
                        style={{backgroundColor:"#a4925a", borderColor:"#a4925a"}} 
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Saving...' : 'Done Editing'}
                    </Button>{' '}
                    <Link to ="/questionTable"><Button style = {{backgroundColor:"#a4925a", borderColor:"#a4925a"}}>See All Questions in Bank</Button></Link>
                    </div>
                </Form>
                    )}
            
            </>
            ) : (
                <PermissionErrorPage/>
            )
        }
        </div>
    )
    
}
